import React, { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import "./TransformTimeline.css";
import { Button } from "@src/stories/elements/Button/Button";
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import AnimatedPulse from "../AnimatedPulse/AnimatedPulse";

export const TransformTimeline = ({ settings }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const timelineLeftRef = useRef<HTMLDivElement>(null);
    const isMobile = useMobileBreakpoint(settings.settings.mobile_breakpoint || 768);
    
    // State for tracking section visibility and scroll state
    const [activeIndex, setActiveIndex] = useState(0);
    const [isScrollable, setIsScrollable] = useState(true);
    const [isLocked, setIsLocked] = useState(false);
    const [touchStart, setTouchStart] = useState(0);
    const [lastWheelEventTime, setLastWheelEventTime] = useState(0);
    const [isAtLimit, setIsAtLimit] = useState({ top: false, bottom: false });
    const [userScrollDirection, setUserScrollDirection] = useState(null);
    const slideRefs = useRef<HTMLDivElement[]>([]);
    
    // Derive slides/items from settings
    const items = settings.settings.blocks.map((item) => ({
        title: item.settings.name,
        description: item.settings.description,
        icon: item.settings.icon,
    }));
    
    // Scroll progress for animations
    const { scrollYProgress } = useScroll({
        container: scrollContainerRef,
        offset: ["start", "end"]
    });

    // Transform [0,1] scroll progress into [0,100] for the indicator's height
    const indicatorHeightPercent = useTransform(scrollYProgress, [0, 1], ["0%", "100%"]);

    // Get configurable content from settings
    const rightSideTitle = settings.settings.right_side_title || "Accelerate Your Transformation";
    const rightSideButtonText = settings.settings.right_side_button_text || "Learn about MindArc Services";
    const rightSideButtonUrl = settings.settings.right_side_button_url || "#";
    
    // Extract and process HTML content from richtext field
    const rightSideDescription = settings.settings.right_side_description ? 
        settings.settings.right_side_description.replace(/<p>/g, "").replace(/<\/p>/g, "").split("<br><br>") : 
        [
            "MindArc has created products and services to accelerate your digital transformation with the Shopify Platform.",
            "Our solutions ensure your site is built with beautiful aesthetics and ready to perform quickly.",
            "MindArc's accelerator solutions ensure your site is built with beautiful aesthetics and ready perform quickly.",
            "Round out your omni-channel retail experience with powerful telecommunication connectivity x Shopify POS."
        ];
    
    // Check if the section is in viewport
    const isElementInViewport = (el) => {
        if (!el) return false;
        const rect = el.getBoundingClientRect();
        return (
            rect.top <= (window.innerHeight / 2) &&
            rect.bottom >= (window.innerHeight / 2)
        );
    };
    
    // Check if we're at the top or bottom of scrollable content
    const checkScrollLimits = () => {
        if (!scrollContainerRef.current) return { atTop: true, atBottom: false };

        const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
        const atTop = scrollTop <= 5;
        const atBottom = scrollTop >= (scrollHeight - clientHeight - 5);
        
        // Update state if we're at a limit
        setIsAtLimit({ top: atTop, bottom: atBottom });
        
        return { atTop, atBottom };
    };

    // Lock scroll behavior when section is in view
    useEffect(() => {
        const handleScroll = () => {
            if (!sectionRef.current || isLocked) return;
            
            const inView = isElementInViewport(sectionRef.current);
            
            // If section comes into view, lock scrolling
            if (inView) {
                setIsLocked(true);
            }
        };
        
        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isLocked]);

    // Update body overflow style when lock state changes
    useEffect(() => {
        if (isLocked) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        
        return () => {
            document.body.style.overflow = '';
        };
    }, [isLocked]);
    
    // Handle wheel events for locked scrolling
    useEffect(() => {
        if (!isLocked) return;
        
        const handleWheel = (e) => {
            if (!scrollContainerRef.current) return;
            
            // Throttle wheel events to prevent too many updates
            const now = Date.now();
            if (now - lastWheelEventTime < 30) return;
            setLastWheelEventTime(now);
            
            // Update user scroll direction
            setUserScrollDirection(e.deltaY > 0 ? 'down' : 'up');
            
            // Get current scroll position limits
            const { atTop, atBottom } = checkScrollLimits();
            const isScrollingDown = e.deltaY > 0;
            
            // If we're at the limits and trying to scroll beyond, unlock
            if ((atTop && !isScrollingDown) || (atBottom && isScrollingDown)) {
                e.preventDefault(); // Prevent default for this event
                
                // Release the lock
                setIsLocked(false);
                
                // Resume normal scrolling after a small delay to allow the browser to process
                // This is important for the smooth transition
                setTimeout(() => {
                    window.scrollBy({
                        top: isScrollingDown ? 50 : -50,
                        behavior: 'auto'
                    });
                }, 10);
                
                return;
            }
            
            // Otherwise, handle scroll within the container
            e.preventDefault();
            scrollContainerRef.current.scrollBy({
                top: e.deltaY,
                behavior: 'auto'
            });
        };
        
        window.addEventListener('wheel', handleWheel, { passive: false });
        
        return () => {
            window.removeEventListener('wheel', handleWheel);
        };
    }, [isLocked, lastWheelEventTime]);

    // Monitor scroll position of the timeline content
    useEffect(() => {
        if (!isLocked || !scrollContainerRef.current) return;
        
        const handleScroll = () => {
            checkScrollLimits();
        };
        
        scrollContainerRef.current.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.removeEventListener('scroll', handleScroll);
            }
        };
    }, [isLocked]);
    
    // Setup intersection observer to detect active slides
    useEffect(() => {
        if (!scrollContainerRef.current) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const index = parseInt(entry.target.getAttribute('data-index') || '0');
                        setActiveIndex(index);
                    }
                });
            },
            {
                root: scrollContainerRef.current,
                threshold: 0.6,
                rootMargin: '0px'
            }
        );

        // Observe all slide elements
        const slides = document.querySelectorAll('.timeline-slide');
        slides.forEach(slide => observer.observe(slide));

        // Check scrollability
        const checkScrollability = () => {
            if (scrollContainerRef.current) {
                const { scrollHeight, clientHeight } = scrollContainerRef.current;
                setIsScrollable(scrollHeight > clientHeight);
            }
        };

        checkScrollability();
        window.addEventListener('resize', checkScrollability);

        return () => {
            slides.forEach(slide => observer.unobserve(slide));
            window.removeEventListener('resize', checkScrollability);
        };
    }, []);

    // Handle touch events for mobile scrolling
    const handleTouchStart = (e) => {
        if (!isLocked) return;
        setTouchStart(e.targetTouches[0].clientY);
    };
    
    const handleTouchMove = (e) => {
        if (!isLocked || !scrollContainerRef.current) return;
        
        const touchY = e.targetTouches[0].clientY;
        const diff = touchStart - touchY;
        
        // Update user scroll direction 
        setUserScrollDirection(diff > 0 ? 'down' : 'up');
        
        const { atTop, atBottom } = checkScrollLimits();
        
        // If at limits and trying to scroll beyond, allow regular scroll
        if ((atTop && diff < 0) || (atBottom && diff > 0)) {
            setIsLocked(false);
            return;
        }
        
        // Otherwise prevent default scroll and handle within container
        e.preventDefault();
        scrollContainerRef.current.scrollTop += diff;
        setTouchStart(touchY);
    };
    
    // Handle touch end event to ensure proper release
    const handleTouchEnd = (e) => {
        if (!isLocked) return;
        
        const { atTop, atBottom } = isAtLimit;
        
        // If at limit when touch ends and trying to scroll that direction, release lock
        if ((atTop && userScrollDirection === 'up') || (atBottom && userScrollDirection === 'down')) {
            setIsLocked(false);
        }
    };

    // Function to scroll to a specific slide
    const scrollToSlide = (index) => {
        if (scrollContainerRef.current && slideRefs.current[index]) {
            scrollContainerRef.current.scrollTo({
                top: slideRefs.current[index].offsetTop,
                behavior: 'smooth'
            });
        }
    };
    
    // React to changes in lock state - apply or remove a class on the html element
    useEffect(() => {
        if (isLocked) {
            document.documentElement.classList.add('timeline-scroll-locked');
        } else {
            document.documentElement.classList.remove('timeline-scroll-locked');
        }
    }, [isLocked]);
    
    // Center the section when it first becomes locked
    useEffect(() => {
        if (isLocked && sectionRef.current) {
            const sectionRect = sectionRef.current.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            window.scrollTo({
                top: window.scrollY + sectionRect.top - (viewportHeight / 2) + (sectionRect.height / 2),
                behavior: 'smooth'
            });
            
            // Reset scroll position of internal container to top when locking
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTop = 0;
            }
        }
    }, [isLocked]);

    return (
        <div 
            className={`transform-timeline-wrapper relative overflow-hidden ${isLocked ? 'locked' : ''}`}
            ref={sectionRef}
            data-locked={isLocked ? "true" : "false"}
        >
            {/* Top gradient/blush element */}
            <div className="absolute top-0 left-0 w-[300px] h-[200px] bg-gradient-radial from-[#899B30]/20 to-transparent z-10 opacity-50"/>
            
            <div className="container mx-auto relative">
                <div 
                    className="timeline-main flex flex-col lg:flex-row min-h-[80vh]"
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    {/* Left Section: Timeline with scrolling items */}
                    <div 
                        className="timeline-left-section w-full lg:w-1/2 relative" 
                        ref={timelineLeftRef}
                    >
                        <div 
                            className={`timeline-scroll-container h-[80vh] overflow-y-auto snap-y snap-mandatory scroll-smooth ${isScrollable ? 'scrollable' : ''}`}
                            ref={scrollContainerRef}
                            data-testid="timeline-scroll-container"
                        >
                            {/* Vertical Timeline Line - Mobile only */}
                            <div className="timeline-line lg:hidden absolute left-[20px] top-[10%] bottom-[10%] w-[2px] bg-[#E3DBC8]"/>
                            
                            {/* Progress indicator on timeline - Mobile only */}
                            <motion.div 
                                className="timeline-progress lg:hidden absolute left-[20px] top-[10%] w-[2px] bg-[#899B30] z-[5]"
                                style={{ height: indicatorHeightPercent }}
                            />

                            {/* Desktop Timeline Line - at edge of screen */}
                            <div className="timeline-line-desktop hidden lg:block absolute left-0 top-[10%] bottom-[10%] w-[2px] bg-[#E3DBC8]"/>
                            
                            {/* Desktop Progress indicator */}
                            <motion.div 
                                className="timeline-progress-desktop hidden lg:block absolute left-0 top-[10%] w-[2px] bg-[#899B30] z-[5]"
                                style={{ height: indicatorHeightPercent }}
                            />
                            
                            {/* Timeline items */}
                            {items.map((item, index) => (
                                <div
                                    key={index}
                                    ref={el => slideRefs.current[index] = el as HTMLDivElement}
                                    data-index={index}
                                    className="timeline-slide flex flex-col justify-center min-h-[80vh] snap-center py-16 px-8 lg:px-16 relative"
                                >
                                    {/* Timeline dot - mobile version */}
                                    <div 
                                        onClick={() => scrollToSlide(index)}
                                        className={`timeline-node lg:hidden absolute left-[14px] w-[14px] h-[14px] rounded-full z-10 cursor-pointer
                                                   ${activeIndex === index ? 'active bg-[#899B30]' : 'bg-[#E3DBC8]'}`}
                                        style={{ top: '50%', transform: 'translateY(-50%)' }}
                                    />
                                    
                                    {/* Timeline dot - desktop version at edge */}
                                    <div 
                                        onClick={() => scrollToSlide(index)}
                                        className={`timeline-node-desktop hidden lg:block absolute left-[-7px] w-[14px] h-[14px] rounded-full z-10 cursor-pointer
                                                   ${activeIndex === index ? 'active bg-[#899B30]' : 'bg-[#E3DBC8]'}`}
                                        style={{ top: '50%', transform: 'translateY(-50%)' }}
                                    />
                                    
                                    {/* Horizontal connector - showing only for active item */}
                                    {activeIndex === index && (
                                        <div className="horizontal-connector absolute h-[2px] bg-[#E3DBC8] z-[1]"
                                             style={{ 
                                                left: isMobile ? '28px' : '0', 
                                                top: '50%', 
                                                width: isMobile ? 'calc(100% - 40px)' : '100%', 
                                                transform: 'translateY(-50%)' 
                                             }}/>
                                    )}
                                    
                                    {/* Content with conditional margin based on device */}
                                    <motion.div 
                                        className={`timeline-content flex flex-col ${isMobile ? 'ml-[60px]' : 'ml-[20px]'}`}
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ 
                                            opacity: activeIndex === index ? 1 : 0.4,
                                            x: activeIndex === index ? 0 : -10,
                                        }}
                                        transition={{ duration: 0.5 }}
                                    >
                                        <div className="mb-3 flex self-start items-start">
                                            {item.icon && (
                                                <img
                                                    src={item.icon}
                                                    alt={item.title}
                                                    className="w-10 h-10"
                                                />
                                            )}
                                        </div>
                                        <h3 className="mb-4 text-2xl font-semibold text-gray-900 max-w-xs lg:max-w-md">{item.title}</h3>
                                        <p className="text-[16px] leading-relaxed text-gray-700 max-w-xs lg:max-w-md">{item.description}</p>
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                        
                        {/* Dot navigation */}
                        <div className="timeline-navigation hidden lg:flex flex-col items-center gap-3 absolute right-4 top-1/2 transform -translate-y-1/2 z-20">
                            {items.map((_, index) => (
                                <div 
                                    key={index}
                                    onClick={() => scrollToSlide(index)}
                                    className={`w-3 h-3 rounded-full cursor-pointer transition-all duration-300 
                                              ${activeIndex === index ? 'bg-[#899B30] scale-125' : 'bg-[#E3DBC8] hover:bg-[#899B30]/70'}`}
                                />
                            ))}
                        </div>
                        
                        {/* Scroll indicator when locked */}
                        {isLocked && (
                            <div className="scroll-indicators absolute left-1/2 transform -translate-x-1/2 z-20 flex flex-col items-center justify-between h-full pointer-events-none">
                                {/* Top indicator - only show when not at the top */}
                                {!isAtLimit.top && (
                                    <div className="scroll-up-indicator top-8 opacity-60 animate-bounce-slow">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="18 15 12 9 6 15"></polyline>
                                        </svg>
                                    </div>
                                )}
                                
                                {/* Bottom indicator with release hint when at top/bottom */}
                                {isAtLimit.top && (
                                    <div className="absolute top-8 text-center opacity-70 bg-black/20 px-4 py-1 rounded-full text-sm">
                                        Scroll up to exit
                                    </div>
                                )}
                                
                                {/* Bottom indicator - only show when not at the bottom */}
                                {!isAtLimit.bottom && (
                                    <div className="scroll-down-indicator bottom-8 opacity-60 animate-bounce-slow">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="6 9 12 15 18 9"></polyline>
                                        </svg>
                                    </div>
                                )}
                                
                                {/* Bottom exit hint */}
                                {isAtLimit.bottom && (
                                    <div className="absolute bottom-8 text-center opacity-70 bg-black/20 px-4 py-1 rounded-full text-sm">
                                        Scroll down to exit
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    
                    {/* Right Section: Fixed content with blush effect */}
                    <div className="timeline-right-section w-full lg:w-1/2 lg:sticky lg:top-0 h-screen flex justify-center items-center">
                        <div className="right-content-wrapper relative px-6 py-10 lg:p-10 max-w-[460px]">
                            {/* Background blush/gradient effect */}
                            <div className="blush-background absolute inset-0 z-[-1] pointer-events-none">
                                <div className="absolute w-full h-full bg-gradient-radial from-[#f9f6f0]/40 via-[#f1ede4]/30 to-transparent opacity-70"></div>
                                <div className="absolute w-[140%] h-[140%] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                                              bg-gradient-radial from-[#f1ede4]/30 via-[#e9e3d6]/20 to-transparent opacity-60"></div>
                            </div>
                            
                            {/* Animated pulse rings */}
                            <div className="pulse-container absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-full w-full">
                                <AnimatedPulse 
                                    settings={{
                                        borderWidth: 1,
                                        colors: ["#e3dbc8", "#899B30", "#AC7E4C", "#D6CFF8"]
                                    }}
                                />
                            </div>
                            
                            <div className="content-container relative z-10 flex flex-col gap-8">
                                <h2 className="text-[40px] lg:text-[48px] font-bold text-gray-900 leading-tight">{rightSideTitle}</h2>
                                <div className="text-[16px] leading-relaxed text-gray-700">
                                    {rightSideDescription.map((paragraph, i) => (
                                        <p key={i} className="mb-6">{paragraph}</p>
                                    ))}
                                </div>
                                <div className="mt-4">
                                    <Button 
                                        style="primary" 
                                        icon="right-arrow-alt" 
                                        label={rightSideButtonText} 
                                        reverse={true}
                                        link={rightSideButtonUrl}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            {/* CSS for the component */}
            <style jsx global>{`
                .timeline-scroll-locked {
                    overflow: hidden !important;
                }
                
                @keyframes bounce-slow {
                    0%, 100% {
                        transform: translateY(0);
                    }
                    50% {
                        transform: translateY(10px);
                    }
                }
                
                .animate-bounce-slow {
                    animation: bounce-slow 2s infinite;
                }
            `}</style>
        </div>
    );
};

export default TransformTimeline;